import { useQueryClient } from "@tanstack/react-query";

import {
  UserCreate,
  UserCreateRequest,
  UserDeleteRequest,
  UserUpdate,
  UserUpdateRequest,
} from "@nens/rana/api-v1";
import { getRanaApiV1 } from "@nens/rana/api-v1";
import { useMutationWithErrorHandler } from "@nens/ranalib";

export type OrgInfo = { organisationId: string } & NonNullable<UserUpdate>;

export type UpdateUsersAcrossTenant =
  | ({
      type: "update-org-info";
    } & OrgInfo)
  | {
      type: "delete-from-org";
      organisationId: string;
    }
  | ({
      type: "add-to-org";
      organisationId: string;
    } & UserCreate);

export const useUpdateUsersAcrossTenants = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutationWithErrorHandler(async (updates: UpdateUsersAcrossTenant[]) => {
    const promises = updates.map(async (update) => {
      if (update.type === "update-org-info") {
        const { organisationId, ...userUpdate } = update;
        const sendUpdate: UserUpdateRequest = {
          id: userId,
          tenantId: organisationId,
          userUpdate,
        };
        await getRanaApiV1(true).UsersApi.userUpdate(sendUpdate);
      }

      if (update.type === "delete-from-org") {
        const sendDelete: UserDeleteRequest = {
          id: userId,
          tenantId: update.organisationId,
        };
        await getRanaApiV1(true).UsersApi.userDelete(sendDelete);
      }

      if (update.type === "add-to-org") {
        const sendCreate: UserCreateRequest = {
          tenantId: update.organisationId,
          userCreate: {
            id: update.id,
            givenName: update.givenName,
            familyName: update.familyName,
            email: update.email,
            role: update.role,
            identityProviderId: update.identityProviderId,
            active: update.active,
          },
        };
        await getRanaApiV1(true).UsersApi.userCreate(sendCreate);
      }
    });

    await Promise.all(promises);

    // Just invalidate everything. Doing it correctly is way too complicated for this
    await queryClient.invalidateQueries({
      queryKey: [],
      refetchType: "all",
    });
  });
};
